/**
 * Klasyfikacja niepowodzen analizy zdjecia.
 *
 * Do tej pory kazda awaria — blad HTTP, odpowiedz HTML zamiast JSON, timeout,
 * brak sieci — konczyla sie jednym komunikatem obwiniajacym zdjecie. To mylilo
 * wykonawce i niszczylo diagnostyke: awarii trasy API nie dalo sie odroznic od
 * nieczytelnego zdjecia ani w UI, ani w logach.
 *
 * Modul nie zmienia dzialania Vision. Rozdziela wylacznie POWOD niepowodzenia
 * i tekst, ktory widzi uzytkownik.
 */

export type VisionFailureKind =
  /** Limit planu po stronie aplikacji. */
  | 'app_plan_limit'
  /** Limit po stronie dostawcy AI. */
  | 'provider_limit'
  /** Backend nieosiagalny: timeout albo brak sieci. */
  | 'backend_unreachable'
  /** Odpowiedz 2xx, ale nie JSON — najczesciej HTML z fallbacku SPA. Trasa /api/* nie dziala. */
  | 'backend_misrouted'
  /** Backend odpowiedzial bledem albo zglosil, ze AI jest niedostepne. */
  | 'backend_error'
  /** Problem faktycznie po stronie zdjecia (brak pliku, plik za duzy). */
  | 'photo_unreadable';

/** Powody zwracane przez klienta Vision i backend, mapowane na rodzaj awarii. */
const REASON_TO_KIND: Record<string, VisionFailureKind> = {
  // limity aplikacji
  app_daily_limit_reached: 'app_plan_limit',
  app_plan_limit: 'app_plan_limit',
  // limity dostawcy
  provider_quota_exhausted: 'provider_limit',
  quota_exhausted: 'provider_limit',
  rate_limited: 'provider_limit',
  '429': 'provider_limit',
  // transport
  timeout_or_network_error: 'backend_unreachable',
  timeout: 'backend_unreachable',
  network_error: 'backend_unreachable',
  // zla trasa API: 2xx bez JSON-a
  non_json_response: 'backend_misrouted',
  // zdjecie
  missing_image: 'photo_unreadable',
  image_too_large: 'photo_unreadable',
};

/**
 * Zwraca rodzaj awarii dla powodu z klienta Vision.
 * Nieznany powod to `backend_error`, nigdy problem ze zdjeciem — nie wolno
 * obwiniac zdjecia, gdy nie wiadomo, co zawiodlo.
 */
export function classifyVisionFailure(reason?: string | null): VisionFailureKind {
  if (typeof reason !== 'string') return 'backend_error';

  const normalized = reason.trim().toLowerCase();
  if (normalized.length === 0) return 'backend_error';

  const mapped = REASON_TO_KIND[normalized];
  if (mapped) return mapped;

  // Powody nadawane po kodzie HTTP, np. "vision_http_502".
  if (/^vision_http_\d{3}$/.test(normalized)) return 'backend_error';

  return 'backend_error';
}

/** Czy awaria lezy po stronie polaczenia/backendu, a nie zdjecia. */
export function isBackendSideFailure(kind: VisionFailureKind): boolean {
  return kind === 'backend_unreachable' || kind === 'backend_misrouted' || kind === 'backend_error';
}

const MANUAL_FALLBACK = 'Ręczne liczenie działa dalej.';

/**
 * Komunikat dla wykonawcy. Tylko `photo_unreadable` moze wspominac o zdjeciu —
 * awaria backendu nigdy nie moze brzmiec jak "nie odczytano wymiarów ze zdjęcia".
 */
export function getVisionFailureMessage(kind: VisionFailureKind): string {
  switch (kind) {
    case 'app_plan_limit':
      return `Limit analizy zdjęć wykorzystany. ${MANUAL_FALLBACK}`;
    case 'provider_limit':
      return `Limit analizy zdjęć po stronie dostawcy. ${MANUAL_FALLBACK}`;
    case 'backend_unreachable':
      return `Brak połączenia z serwerem analizy. To nie jest problem ze zdjęciem. ${MANUAL_FALLBACK}`;
    case 'backend_misrouted':
      return `Serwer analizy odpowiedział nieprawidłowo (błąd konfiguracji API). To nie jest problem ze zdjęciem. ${MANUAL_FALLBACK}`;
    case 'backend_error':
      return `Analiza zdjęcia jest chwilowo niedostępna po stronie serwera. To nie jest problem ze zdjęciem. ${MANUAL_FALLBACK}`;
    case 'photo_unreadable':
      return `Nie udało się użyć tego zdjęcia. Spróbuj innego kadru lub mniejszego pliku. ${MANUAL_FALLBACK}`;
  }
}

/** Zwarty wpis diagnostyczny do logu operacyjnego, bez tresci zdjecia i naglowkow auth. */
export function buildVisionDiagnosticCode(input: {
  reason?: string | null;
  httpStatus?: number | null;
  contentType?: string | null;
}): string {
  const parts = [`reason=${input.reason?.trim() || 'unknown'}`];
  if (typeof input.httpStatus === 'number' && Number.isFinite(input.httpStatus)) {
    parts.push(`status=${String(input.httpStatus)}`);
  }
  if (input.contentType) {
    // Sam typ MIME, bez charset i parametrow.
    parts.push(`type=${input.contentType.split(';')[0]?.trim() ?? ''}`);
  }
  return parts.join(' ');
}
